import { useAuthStore } from "@/stores/auth";
import type {
  AuthPayload,
  AuthTokens,
  EventLogBreakdownBucket,
  EventLogFilters,
  EventLogOverview,
  EventLogSeriesPoint,
  PaginatedRenderAssets,
  PaginatedEventLogs,
  RegisterPayload,
  RenderAssetDetail,
  RenderFilters,
  RenderMeta,
  RenderTexturePreview,
  RenderTexturePriceUpdateResult,
  RuntimeSettings,
  StatsOverview,
  UserProfile,
  UserUpdateRequest,
} from "@/lib/api-types";

const API_BASE = import.meta.env.VITE_API_BASE || "";

type RequestOptions = RequestInit & {
  auth?: boolean;
  retryOnUnauthorized?: boolean;
};

let refreshRequest: Promise<AuthTokens> | null = null;

function buildSessionExpiredError(message?: string): Error {
  return new Error(
    message?.trim() || "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
  );
}

function redirectToLogin(reason = "expired") {
  if (typeof window === "undefined") {
    return;
  }

  const loginURL = new URL("/login", window.location.origin);
  const nextPath = `${window.location.pathname}${window.location.search}`;

  if (nextPath && !nextPath.startsWith("/login")) {
    loginURL.searchParams.set("next", nextPath);
  }
  if (reason) {
    loginURL.searchParams.set("reason", reason);
  }

  window.location.replace(`${loginURL.pathname}${loginURL.search}`);
}

function expireSession(message?: string): never {
  useAuthStore.getState().logout();
  redirectToLogin("expired");
  throw buildSessionExpiredError(message);
}

async function refreshAccessToken(): Promise<AuthTokens> {
  if (refreshRequest) {
    return refreshRequest;
  }

  const { isAuthenticated, refreshToken, updateTokens } = useAuthStore.getState();
  if (!isAuthenticated || !refreshToken) {
    throw buildSessionExpiredError();
  }

  refreshRequest = (async () => {
    const response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      throw buildSessionExpiredError(payload?.message);
    }

    const tokens = (payload?.data ?? payload) as AuthTokens;
    if (!tokens?.accessToken || !tokens?.refreshToken) {
      throw new Error("Invalid refresh token response");
    }

    updateTokens(tokens.accessToken, tokens.refreshToken);
    return tokens;
  })().finally(() => {
    refreshRequest = null;
  });

  return refreshRequest;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return requestWithFallback(path, undefined as T, options);
}

async function requestWithFallback<T>(
  path: string,
  fallback: T,
  options: RequestOptions = {},
): Promise<T> {
  const { auth = true, retryOnUnauthorized = true, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);

  if (!finalHeaders.has("Content-Type") && !(rest.body instanceof FormData)) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (auth) {
    const token = useAuthStore.getState().token;
    if (token) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: finalHeaders,
  });

  const payload = await response.json().catch(() => null);
  if (response.status === 401 && auth && retryOnUnauthorized) {
    try {
      await refreshAccessToken();
      return requestWithFallback(path, fallback, {
        ...options,
        retryOnUnauthorized: false,
      });
    } catch (error) {
      if (error instanceof Error) {
        expireSession(error.message);
      }
      expireSession();
    }
  }

  if (!response.ok || payload?.success === false) {
    if (response.status === 401 && auth) {
      expireSession(payload?.message);
    }
    throw new Error(
      payload?.message || `Request failed with status ${response.status}`,
    );
  }

  if (
    payload &&
    typeof payload === "object" &&
    "success" in payload &&
    payload.success === true &&
    !("data" in payload)
  ) {
    return fallback;
  }

  return (payload?.data ?? payload ?? fallback) as T;
}

function toSearchParams<T extends object>(filters: T): string {
  const params = new URLSearchParams();
  Object.entries(filters as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : "";
}

function toEventLogSearchParams(filters: EventLogFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    if (key === "labels" && value && typeof value === "object") {
      Object.entries(value as Record<string, string>).forEach(
        ([labelKey, labelValue]) => {
          if (!labelKey || !labelValue) {
            return;
          }
          params.set(`label.${labelKey}`, labelValue);
        },
      );
      return;
    }
    params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const api = {
  login: (username: string, password: string) =>
    request<AuthPayload>("/api/auth/login", {
      auth: false,
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  register: (payload: {
    username: string;
    email: string;
    password: string;
  }) =>
    request<RegisterPayload>("/api/auth/register", {
      auth: false,
      method: "POST",
      body: JSON.stringify(payload),
    }),

  me: () => request<UserProfile>("/api/auth/me"),

  refresh: (refreshToken: string) =>
    request<AuthTokens>("/api/auth/refresh", {
      auth: false,
      method: "POST",
      body: JSON.stringify({ refreshToken }),
    }),

  getAssetLibraryClothesMeta: () =>
    request<RenderMeta>("/api/asset-library/clothes/meta", {
      auth: false,
    }),

  getAssetLibraryClothesItems: (filters: RenderFilters) =>
    request<PaginatedRenderAssets>(
      `/api/asset-library/clothes/items${toSearchParams(filters)}`,
      {
        auth: false,
      },
    ),

  getAssetLibraryClothesItem: (id: string) =>
    request<RenderAssetDetail>(`/api/asset-library/clothes/items/${id}`, {
      auth: false,
    }),

  getAssetLibraryClothesTexturePreview: (id: string, textureIndex: number) =>
    request<RenderTexturePreview>(
      `/api/asset-library/clothes/items/${encodeURIComponent(id)}/textures/${textureIndex}/preview`,
      {
        auth: false,
      },
    ),

  getAssetLibraryClothesItemByIdentity: (params: {
    collection: string;
    gender: string;
    kind: string;
    slotId: number;
    drawableLocalIndex: number;
    meshPartIndex: number;
  }) =>
    request<RenderAssetDetail>(
      `/api/asset-library/clothes/resolve${toSearchParams(params)}`,
      {
        auth: false,
      },
    ),

  updateAssetLibraryClothesTexturePrice: (
    id: string,
    textureIndex: number,
    price: number,
    priceType: "money" | "coin" | "hidden",
    reason: string,
    scope: "texture" | "all",
  ) =>
    request<RenderTexturePriceUpdateResult>(
      `/api/asset-library/clothes/items/${id}/textures/${textureIndex}/price`,
      {
        method: "PUT",
        body: JSON.stringify({ price, priceType, reason, scope }),
      },
    ),

  getEventLogOverview: (filters: EventLogFilters) =>
    request<EventLogOverview>(
      `/api/event-logs/overview${toEventLogSearchParams(filters)}`,
    ),

  getEventLogEvents: (filters: EventLogFilters) =>
    request<PaginatedEventLogs>(
      `/api/event-logs/events${toEventLogSearchParams(filters)}`,
    ),

  getEventLogBreakdown: (
    filters: EventLogFilters,
    field: string,
    metric: "count" | "amount" | "quantity",
    limit = 8,
  ) =>
    requestWithFallback<EventLogBreakdownBucket[]>(
      `/api/event-logs/breakdown${toEventLogSearchParams({
        ...filters,
        field,
        metric,
        limit,
      } as EventLogFilters & {
        field: string;
        metric: string;
        limit: number;
      })}`,
      [],
    ),

  getEventLogSeries: (
    filters: EventLogFilters,
    metric: "count" | "amount" | "quantity",
    interval: "hour" | "day" | "month",
    groupBy?: string,
  ) =>
    requestWithFallback<EventLogSeriesPoint[]>(
      `/api/event-logs/series${toEventLogSearchParams({
        ...filters,
        metric,
        interval,
        groupBy,
      } as EventLogFilters & {
        metric: string;
        interval: string;
        groupBy?: string;
      })}`,
      [],
    ),

  getStats: () => request<StatsOverview>("/api/stats/overview"),

  getRuntimeSettings: () => request<RuntimeSettings>("/api/settings/runtime"),

  getUsers: () => request<UserProfile[]>("/api/users"),

  updateUser: (id: number, updates: UserUpdateRequest) =>
    request<UserProfile>(`/api/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),
};
